/**
 * entitlements.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Vinarai subscription tier definitions and entitlement resolution.
 *
 * Canonical tiers (ascending):
 *   glass       — Free tier. "Getting started with wine" persona. Basic
 *                 logging, small AI allowance, attend-only events.
 *   decanter    — Mid tier. "Serious hobbyist" persona. Bigger cellar, more
 *                 AI, can host small tastings.
 *   sommelier   — Top tier. "True sommelier" persona. Essentially no day-to-
 *                 day guardrails — high monthly caps, unlimited events.
 *
 * Kept alongside:
 *   beta        — Existing invited beta testers. Full unlock until public
 *                 launch, at which point we sweep them into sommelier.
 *
 * Legacy aliases (resolved at runtime so existing Firestore values keep
 * working): 'free' → glass, 'pro' → decanter, 'collector' → sommelier.
 *
 * See docs/tiers-plan.md for the full plan — persona reasoning, cost caps,
 * merchant track, admin tooling, and phase-by-phase rollout.
 *
 * Adding a new gated feature:
 *   1. Add a key to TIER_FEATURES for each tier below.
 *   2. Destructure it from useEntitlements() in the page that needs it.
 *   3. Show <UpgradeWall feature="..." /> when the entitlement is false.
 *
 * Wiring up payments (Stripe, RevenueCat, etc.) later:
 *   - The `tier` field in profiles/{uid} and allowedUsers/{email} is the
 *     source of truth. Your webhook writes the new tier string there.
 *   - No other code needs to change.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Legacy tier aliasing ─────────────────────────────────────────────────────
// Existing users in Firestore have tier: 'free' | 'pro' | 'collector'. Map
// those to the new canonical names at resolve time so nothing breaks.
// 'merchant' → 'merchant_starter' for any Firestore docs written before
// the sub-tier split landed.
//
// Note: 'pro' is intentionally a CONSUMER alias (→ decanter) here. The
// merchant Pro tier always carries the `merchant_pro` prefix in writes,
// so the bare 'pro' token can stay pointed at the legacy consumer name
// without ambiguity.
export const TIER_ALIASES = {
  free:      'glass',
  pro:       'decanter',
  collector: 'sommelier',
  merchant:  'merchant_starter',
}

function canonicalTier(tier) {
  if (!tier) return 'glass'
  return TIER_ALIASES[tier] || tier
}

// ── Merchant base feature set ─────────────────────────────────────────────────
// Shared by all three merchant sub-tiers. Spread into each tier entry below.
// Not exported — consumers should reference the specific tier key.
const MERCHANT_BASE = {
  color: '#2E5E3E',
  maxWines: Infinity,
  photosPerWine: Infinity,
  bulkUpload: true,
  bulkUploadMonthlyRuns: Infinity,
  bulkUploadMaxWinesPerRun: 500,
  aiSommelier: true,
  sommelierMonthlyLimit: 500,
  wineInsights: true,
  cellarAnalysis: true,
  cellarAnalysisMonthlyLimit: 5,
  classicWineAnalysis: true,
  classicWineAnalysisLifetimeLimit: Infinity,
  classicWineAnalysisMonthlyLimit: Infinity,
  wineOfTheDay: true,
  dineOut: true,
  dineOutLifetimeLimit: Infinity,
  dineOutMonthlyLimit: Infinity,
  events: true,
  canAttendEvents: true,
  canHostEvents: true,
  hostActiveEventsMax: Infinity,
  hostGuestsPerEventMax: Infinity,
  maxFriends: Infinity,
  maxConnections: Infinity,
  aiLabelScan: true,
  aiTagSuggestions: true,
  stockLabel: true,
}

// ── Tier feature matrix ───────────────────────────────────────────────────────
// Numeric caps are MONTHLY unless explicitly suffixed. Lifetime counts act as
// trial allowances that convert without a renewal. `Infinity` means uncapped
// for practical purposes — we still keep the server-side daily safety net.

export const TIER_FEATURES = {
  glass: {
    name: 'Glass',
    color: '#888888',        // stone — entry tier

    // Cellar
    maxWines: 50,
    // Free tier keeps the label scan as their single photo — the
    // "1 photo" IS the wine label. No additional gallery photos.
    // Decanter+ unlock the stacked-card gallery (see decanter/sommelier).
    photosPerWine: 1,
    bulkUpload: false,
    bulkUploadMonthlyRuns: 0,
    bulkUploadMaxWinesPerRun: 0,

    // AI features
    aiSommelier: true,
    sommelierMonthlyLimit: 5,
    wineInsights: false,
    cellarAnalysis: false,
    cellarAnalysisMonthlyLimit: 0,
    classicWineAnalysis: true,
    classicWineAnalysisLifetimeLimit: 3,
    classicWineAnalysisMonthlyLimit: 0,
    wineOfTheDay: false,
    dineOut: true,
    dineOutLifetimeLimit: 2,
    dineOutMonthlyLimit: 0,

    // Events
    events: true,            // attend-only below
    canAttendEvents: true,
    canHostEvents: false,
    hostActiveEventsMax: 0,
    hostGuestsPerEventMax: 0,

    // Sharing
    // maxFriends    = outgoing+incoming social connections (type: 'friend')
    // maxHousehold  = total household seats INCLUDING the host. 0 = no household.
    // maxConnections kept as a legacy sum cap; new code reads the split fields.
    maxFriends: 5,
    maxHouseholdSeats: 0,
    maxConnections: 5,

    // Label scanning — AI vision + OCR fallback available to all tiers.
    // Set false only to create a fully-offline no-AI tier in the future.
    aiLabelScan: true,
    // aiTagSuggestions enabled for Glass (free) tier — was false, which
    // meant new free-tier accounts saw zero AI tag suggestions when
    // adding a wine despite the localStorage pref being on. The
    // suggestion is a small engagement nudge (1-3 free-form tags) and
    // there's no tier-differentiator reason to gate it. Re-flag to
    // false here if a future tier strategy wants to upsell it.
    aiTagSuggestions: true,

    // Visual polish — paid-tier perk; Glass keeps the raw photo (or
    // text fallback when no photo) without the curated stock label.
    stockLabel: false,
  },

  decanter: {
    name: 'Decanter',
    color: '#7B2040',        // burgundy — serious-hobbyist tier

    // Cellar
    maxWines: 500,
    // Decanter unlocks the stacked-card gallery: label + up to 5 extra
    // photos = 6 total. Captions, reordering, delete all available.
    photosPerWine: 6,
    bulkUpload: true,
    bulkUploadMonthlyRuns: 1,
    bulkUploadMaxWinesPerRun: 50,

    // AI features
    aiSommelier: true,
    sommelierMonthlyLimit: 50,
    wineInsights: true,
    cellarAnalysis: true,
    cellarAnalysisMonthlyLimit: 1,
    classicWineAnalysis: true,
    classicWineAnalysisLifetimeLimit: Infinity,
    classicWineAnalysisMonthlyLimit: 10,
    wineOfTheDay: true,
    dineOut: true,
    dineOutLifetimeLimit: Infinity,
    dineOutMonthlyLimit: 10,

    // Events
    events: true,
    canAttendEvents: true,
    canHostEvents: true,
    hostActiveEventsMax: 2,
    hostGuestsPerEventMax: 10,

    // Sharing — unlimited friends, host + 2 household members (3 seats total)
    maxFriends: Infinity,
    maxHouseholdSeats: 3,
    maxConnections: Infinity,

    aiLabelScan: true,
    aiTagSuggestions: true,

    // Visual polish — Vinarai-styled "house label" rendering of each wine,
    // shown as the primary visual with the user's uploaded photo as #2 in
    // a swipeable carousel. Available to Decanter and Sommelier so paid
    // users have a curated cellar look immediately.
    stockLabel: true,
  },

  sommelier: {
    name: 'Sommelier',
    color: '#C4965A',        // gold — top consumer tier

    // Cellar
    maxWines: Infinity,
    photosPerWine: Infinity,
    bulkUpload: true,
    bulkUploadMonthlyRuns: Infinity,
    bulkUploadMaxWinesPerRun: 500,

    // AI features
    aiSommelier: true,
    sommelierMonthlyLimit: 500,
    wineInsights: true,
    cellarAnalysis: true,
    cellarAnalysisMonthlyLimit: 5,
    classicWineAnalysis: true,
    classicWineAnalysisLifetimeLimit: Infinity,
    classicWineAnalysisMonthlyLimit: Infinity,
    wineOfTheDay: true,
    dineOut: true,
    dineOutLifetimeLimit: Infinity,
    dineOutMonthlyLimit: Infinity,

    // Events
    events: true,
    canAttendEvents: true,
    canHostEvents: true,
    hostActiveEventsMax: Infinity,
    hostGuestsPerEventMax: Infinity,

    // Sharing — unlimited friends, host + 5 household members (6 seats total)
    maxFriends: Infinity,
    maxHouseholdSeats: 6,
    maxConnections: Infinity,

    aiLabelScan: true,
    aiTagSuggestions: true,

    // Visual polish — Vinarai stock-label rendering as the primary
    // visual, with the user's uploaded photo as carousel #2.
    stockLabel: true,
  },

  // Merchant tier — wine shops running tasting events. Mirrors Sommelier's
  // feature set but with 10 staff seats so a small wine shop can have the
  // owner + 2-3 floor staff + a few off-floor seats (events ops, accounting,
  // social media manager) without bumping into the cap. Was 3 originally —
  // raised to 10 as the first quick-win in the merchant strategy sprint to
  // unblock chain/multi-staff onboarding without needing a new tier yet.
  // Resolved from `accountType: 'merchant'` in App.jsx.
  merchant: {
    name: 'Merchant',
    color: '#2E5E3E',        // forest — distinct from consumer tiers

    // Cellar
    maxWines: Infinity,
    photosPerWine: Infinity,
    bulkUpload: true,
    bulkUploadMonthlyRuns: Infinity,
    bulkUploadMaxWinesPerRun: 500,

    // AI features
    aiSommelier: true,
    sommelierMonthlyLimit: 500,
    wineInsights: true,
    cellarAnalysis: true,
    cellarAnalysisMonthlyLimit: 5,
    classicWineAnalysis: true,
    classicWineAnalysisLifetimeLimit: Infinity,
    classicWineAnalysisMonthlyLimit: Infinity,
    wineOfTheDay: true,
    dineOut: true,
    dineOutLifetimeLimit: Infinity,
    dineOutMonthlyLimit: Infinity,

    // Events — merchants lean on events heavily (tasting nights)
    events: true,
    canAttendEvents: true,
    canHostEvents: true,
    hostActiveEventsMax: Infinity,
    hostGuestsPerEventMax: Infinity,

    // Sharing — shop owner + up to 9 staff (10 seats total)
    maxFriends: Infinity,
    maxHouseholdSeats: 10,
    maxConnections: Infinity,

    aiLabelScan: true,
    aiTagSuggestions: true,

    // Visual polish — merchants get the same Sommelier perks
    stockLabel: true,
  },

  // ── Merchant sub-tiers ───────────────────────────────────────────────────
  // Resolved from profiles/{uid}.merchantTier (set by Admin). Falls back
  // to merchant_starter for any account with accountType='merchant' that
  // lacks a merchantTier field. The legacy 'merchant' alias in TIER_ALIASES
  // maps old Firestore docs to merchant_starter automatically.

  // Listed tier — free directory entry. Shop has a /shop/{slug} page +
  // basic info (hours, address, phone) + customer follows, but cannot
  // host events, has no inventory tools beyond a 10-wine sample, and
  // gets none of the analytics / live broadcast / staff-seat perks.
  // The user's PERSONAL cellar still gets glass-tier consumer perks
  // for their own usage (50 wines etc.); the merchant_free shape just
  // governs what the merchant console exposes.
  merchant_free: {
    name: 'Listed',
    color: '#666666',          // stone — entry merchant tier
    monthlyPrice: 0,

    // Cellar / shop inventory
    maxWines: 10,
    photosPerWine: 2,
    bulkUpload: false,
    bulkUploadMonthlyRuns: 0,
    bulkUploadMaxWinesPerRun: 0,

    // AI features — minimal
    aiSommelier: true,
    sommelierMonthlyLimit: 5,
    wineInsights: false,
    cellarAnalysis: false,
    cellarAnalysisMonthlyLimit: 0,
    classicWineAnalysis: true,
    classicWineAnalysisLifetimeLimit: 3,
    classicWineAnalysisMonthlyLimit: 0,
    wineOfTheDay: false,
    dineOut: true,
    dineOutLifetimeLimit: 2,
    dineOutMonthlyLimit: 0,

    // Events — Listed cannot host
    events: true,
    canAttendEvents: true,
    canHostEvents: false,
    hostActiveEventsMax: 0,
    hostGuestsPerEventMax: 0,

    // Sharing — owner only, no staff seats
    maxFriends: Infinity,
    maxHouseholdSeats: 1,
    maxConnections: Infinity,

    aiLabelScan: true,
    aiTagSuggestions: false,
    stockLabel: false,

    // Merchant-specific gates — all off
    merchantWotdScheduling: false,
    merchantAnalytics: false,
    merchantAudienceDashboard: false,
    merchantPrioritySort: false,
    merchantPostTastingEmail: false,
    merchantStorefrontBrandingHideable: false,
  },

  merchant_starter: {
    ...MERCHANT_BASE,
    name: 'Starter',
    monthlyPrice: 49,
    annualPrice: 490,
    maxHouseholdSeats: 2,
    merchantWotdScheduling: false,
    merchantAnalytics: false,
    merchantAudienceDashboard: false,
    merchantPrioritySort: false,
    merchantPostTastingEmail: false,
    // The "Powered by Vinarai" footer + "Join Vinarai Free" CTA on the
    // public /shop/{slug} page is on by default for every shop. Lower
    // tiers can't hide it (free distribution for us, no escape hatch).
    // Pro can flip it off to give their storefront a fully white-label
    // look. Defense in depth: Shop.jsx checks BOTH the per-shop flag
    // AND the shop's merchantTier (denormalized on the shop doc) before
    // hiding the branding — so a stale flag from a downgraded shop
    // doesn't keep the branding hidden.
    merchantStorefrontBrandingHideable: false,
  },

  merchant_growth: {
    ...MERCHANT_BASE,
    name: 'Growth',
    monthlyPrice: 199,
    annualPrice: 1990,
    maxHouseholdSeats: 5,
    merchantWotdScheduling: true,
    merchantAnalytics: true,
    merchantAudienceDashboard: false,
    merchantPrioritySort: false,
    merchantPostTastingEmail: false,
    merchantStorefrontBrandingHideable: false,
  },

  merchant_pro: {
    ...MERCHANT_BASE,
    name: 'Pro',
    monthlyPrice: 499,
    annualPrice: 4990,
    maxHouseholdSeats: 10,
    merchantWotdScheduling: true,
    merchantAnalytics: true,
    merchantAudienceDashboard: true,
    merchantPrioritySort: true,
    merchantPostTastingEmail: true,
    merchantStorefrontBrandingHideable: true,
  },

  // Enterprise — custom pricing, full unlock. Provisioned by us when
  // we sign a multi-location chain or importer. Caps are uncapped;
  // anything bespoke (custom domain, white-label, Mux, API access)
  // is wired up per-tenant at the integration layer.
  merchant_enterprise: {
    ...MERCHANT_BASE,
    name: 'Enterprise',
    monthlyPrice: null,        // negotiated
    annualPrice: null,
    maxHouseholdSeats: Infinity,
    merchantWotdScheduling: true,
    merchantAnalytics: true,
    merchantAudienceDashboard: true,
    merchantPrioritySort: true,
    merchantPostTastingEmail: true,
    merchantStorefrontBrandingHideable: true,
  },

  // Invited beta testers. Full unlock. At public launch, sweep these users
  // to `sommelier` (or gift an annual on us).
  beta: {
    name: 'Beta',
    color: '#9B6FD4',        // violet — distinct from consumer-tier colours

    // Cellar
    maxWines: Infinity,
    photosPerWine: Infinity,
    bulkUpload: true,
    bulkUploadMonthlyRuns: Infinity,
    bulkUploadMaxWinesPerRun: 500,

    // AI features
    aiSommelier: true,
    sommelierMonthlyLimit: Infinity,
    wineInsights: true,
    cellarAnalysis: true,
    cellarAnalysisMonthlyLimit: Infinity,
    classicWineAnalysis: true,
    classicWineAnalysisLifetimeLimit: Infinity,
    classicWineAnalysisMonthlyLimit: Infinity,
    wineOfTheDay: true,
    dineOut: true,
    dineOutLifetimeLimit: Infinity,
    dineOutMonthlyLimit: Infinity,

    // Events
    events: true,
    canAttendEvents: true,
    canHostEvents: true,
    hostActiveEventsMax: Infinity,
    hostGuestsPerEventMax: Infinity,

    // Sharing — beta testers get unlimited everything
    maxFriends: Infinity,
    maxHouseholdSeats: Infinity,
    maxConnections: Infinity,

    aiLabelScan: true,
    aiTagSuggestions: true,

    // Visual polish — beta testers see every paid feature
    stockLabel: true,
  },
}

// ── Merchant tier helpers ─────────────────────────────────────────────────────
// Order matters — used by Admin tier-select dropdowns and as a rank for
// upgrade/downgrade comparisons. Listed → Enterprise, ascending.
export const MERCHANT_TIERS = [
  'merchant_free',
  'merchant_starter',
  'merchant_growth',
  'merchant_pro',
  'merchant_enterprise',
]

// ── Staff tier inheritance ────────────────────────────────────────────────────
// When a user joins a shop's staff (merchant_staff connection), they get a
// consumer tier as a perk — paid for by their employer's subscription. The
// shop's tier determines which consumer tier they inherit. The values are a
// deliberate "thank you for being on the team" ladder, with Pro getting full
// Sommelier perks so a shop owner upgrading to Pro is buying more than just
// shop features — they're upgrading their team's personal cellar perks too.
//
// Inherited tier resolution (in App.jsx):
//   1. User has accountType === 'personal'
//   2. User has at least one active connection of type 'merchant_staff'
//   3. Look up the shop owner's profile.merchantTier
//   4. inheritedTier = STAFF_TIER_BY_SHOP[merchantTier]
//   5. Effective tier = max(personalTier, inheritedTier) — staff keeps their
//      paid personal tier if higher than what the shop provides, otherwise
//      gets bumped up.
export const STAFF_TIER_BY_SHOP = {
  merchant_free:       'glass',      // Listed has no staff seats; if we
                                     //   ever provision one, default to free.
  merchant_starter:    'decanter',
  merchant_growth:     'decanter',
  merchant_pro:        'sommelier',
  merchant_enterprise: 'sommelier',
}

// Maximum number of admin-tier accounts per shop, COUNTING the owner.
// The owner is implicitly admin (slot 1); promoting one staff member
// fills slot 2. This keeps the "who can change the plan / storefront /
// invite staff" surface small — most shops want one or two trusted
// hands, not the whole team.
export const MAX_SHOP_ADMINS = 2

// Returns the consumer tier a staff member of a given shop tier inherits.
// Falls back to 'glass' for unrecognized shop tiers (e.g. legacy 'merchant').
export function getStaffTierFromShop(shopTier) {
  if (!shopTier) return 'glass'
  return STAFF_TIER_BY_SHOP[shopTier] || STAFF_TIER_BY_SHOP[TIER_ALIASES[shopTier]] || 'glass'
}

// Tier index for max() comparisons. Higher number = better tier. Used when
// a staff member has a paid personal tier AND inherits one from their shop —
// we keep whichever is more generous so a Sommelier-paying user doesn't
// get downgraded by joining a Starter shop.
export const TIER_ORDER = {
  glass: 0,
  decanter: 1,
  sommelier: 2,
  beta: 2, // beta-equivalent to sommelier for ordering purposes
}

export function maxTier(a, b) {
  const aRank = TIER_ORDER[canonicalTier(a)] ?? -1
  const bRank = TIER_ORDER[canonicalTier(b)] ?? -1
  return aRank >= bRank ? a : b
}

// ── Per-feature override resolver ────────────────────────────────────────────
// customFeatures supports two shapes per entry:
//
//   1. Legacy (pre-April 2026):   { cellarAnalysis: true }
//   2. Current (with expiry):     { cellarAnalysis: { value: true, expiresAt: '2026-05-01T...' } }
//
// The expiring shape lets admins comp a single feature for a window without
// bumping the user's tier ("30 days of Cellar Analysis, stay on Glass"). If
// `expiresAt` is past, the override is silently dropped and the user falls
// back to the tier default. Legacy bools keep working forever — they just
// never expire.
//
// Exported so the Admin UI can normalize values back to primitives for
// display, and so smoke tests can exercise the logic without the React hook.
export function resolveOverride(raw, now = new Date()) {
  if (raw === undefined || raw === null) return undefined
  // Legacy primitive shape — no expiry, return as-is.
  if (typeof raw !== 'object') {
    // `"unlimited"` is the Firestore-safe sentinel for numeric-cap overrides
    // that mean "no cap" (Infinity doesn't survive JSON serialization).
    // Consumers read numeric caps with strict `=== Infinity` checks, so we
    // map it back here at the resolver layer and nothing downstream changes.
    if (raw === 'unlimited') return Infinity
    return raw
  }
  // Rich shape — must have `value`. Anything else is malformed and ignored.
  if (!('value' in raw)) return undefined
  const { value, expiresAt } = raw
  if (expiresAt) {
    const t = new Date(expiresAt).getTime()
    if (Number.isFinite(t) && t < now.getTime()) return undefined
  }
  // Same "unlimited" sentinel handling inside the rich shape.
  if (value === 'unlimited') return Infinity
  return value
}

// ── Entitlement resolver ──────────────────────────────────────────────────────
/**
 * Given a tier string, optional expiry, and optional per-user feature overrides,
 * returns the full entitlement object.
 *
 * Tier values are canonicalized via TIER_ALIASES, so legacy values
 * (free/pro/collector) are accepted and resolved to glass/decanter/sommelier.
 * If the tier has expired, falls back to `glass`.
 *
 * customFeatures — a plain object stored on the user's profile / allowedUsers doc.
 * Each entry is either a primitive (legacy) or { value, expiresAt } (current).
 * Expired overrides are dropped; everything else takes precedence over the
 * tier default.
 *
 * @param {string|null}  tier           - 'glass' | 'decanter' | 'sommelier' | 'beta' (or legacy alias)
 * @param {string|null}  tierExpiresAt  - ISO 8601 string or null (no expiry)
 * @param {object|null}  customFeatures - Per-user overrides (optional)
 * @returns {object}  Entitlement object with all feature flags + metadata
 */
export function computeEntitlements(tier, tierExpiresAt, customFeatures = null) {
  const now = new Date()
  const isExpired = tierExpiresAt ? new Date(tierExpiresAt) < now : false
  // On expiry, fall back to the free tier (Glass). Legacy tier names
  // (free/pro/collector) are mapped to canonical ones at read time.
  const rawTier = isExpired ? 'glass' : (tier || 'glass')
  const canonical = canonicalTier(rawTier)
  // If the canonical tier isn't a known key (e.g. a typo or retired tier
  // still sitting on a profile), fall back to Glass so `tier` and `features`
  // stay in sync instead of reporting a bogus tier name with free caps.
  const effectiveTier = TIER_FEATURES[canonical] ? canonical : 'glass'
  const features = TIER_FEATURES[effectiveTier]

  // Merge custom overrides on top of tier defaults. Each entry runs through
  // resolveOverride() so the legacy bool shape and the new { value, expiresAt }
  // shape both work, and expired per-feature grants quietly fall off.
  let merged = features
  if (customFeatures) {
    const resolved = {}
    for (const [k, v] of Object.entries(customFeatures)) {
      const val = resolveOverride(v, now)
      if (val !== undefined) resolved[k] = val
    }
    if (Object.keys(resolved).length > 0) {
      merged = { ...features, ...resolved }
    }
  }

  return {
    ...merged,
    tier: effectiveTier,
    isExpired,
    tierExpiresAt: tierExpiresAt ?? null,
    customFeatures: customFeatures ?? null,

    // Helpers — resolved against canonical tier names
    isPaid: effectiveTier === 'decanter' || effectiveTier === 'sommelier',
    isBeta: effectiveTier === 'beta',
    isFree: effectiveTier === 'glass',
    isMerchantTier: effectiveTier.startsWith('merchant_'),
    merchantTierName: effectiveTier.startsWith('merchant_') ? (TIER_FEATURES[effectiveTier]?.name ?? null) : null,

    // Returns the minimum canonical tier name where a feature is unlocked.
    // Iterates in the order defined in TIER_FEATURES (glass → decanter → …).
    requiredTierFor(feature) {
      for (const [t, f] of Object.entries(TIER_FEATURES)) {
        if (f[feature] === true || (typeof f[feature] === 'number' && f[feature] > 0)) {
          return t
        }
      }
      return 'sommelier'
    },
  }
}

// ── Free-tier defaults (exported for convenience) ────────────────────────────
export const FREE_ENTITLEMENTS = computeEntitlements('glass', null)

// ── Live entitlements hook ────────────────────────────────────────────────────
// computeEntitlements() resolves expiry against "now" whenever it's called,
// but React won't re-render a component just because real-world time passed.
// Without this hook, a user sitting on a page at 11:59 PM with a tier that
// expires at midnight would keep their paid UI until they navigated or
// reloaded. We fix that two ways:
//
//   1. If the expiry is within ~24 days (setTimeout's practical max), schedule
//      a precise timer to force a re-render at that exact moment.
//   2. On tab refocus (visibilitychange → visible), force a re-render — this
//      covers the laptop-closed-overnight case where setTimeout doesn't fire
//      at the expected time.
//
// Usage is a drop-in replacement for computeEntitlements inside function
// components:
//   const entitlements = useEntitlements(userTier, tierExpiresAt, customFeatures)
import { useEffect, useState } from 'react'

// setTimeout clamps delays > ~24.8 days. We use a slightly smaller ceiling
// so the timer fires early-enough to reschedule on the next effect run.
const MAX_TIMEOUT_MS = 2_000_000_000

export function useEntitlements(tier, tierExpiresAt, customFeatures = null) {
  const [, setTick] = useState(0)
  const bump = () => setTick(t => t + 1)

  // Precise timeout at the expiry moment.
  useEffect(() => {
    if (!tierExpiresAt) return
    const expMs = new Date(tierExpiresAt).getTime()
    if (!Number.isFinite(expMs)) return
    const delay = expMs - Date.now()
    if (delay <= 0) return
    if (delay > MAX_TIMEOUT_MS) return   // too far out — rely on focus handler
    const id = setTimeout(bump, delay)
    return () => clearTimeout(id)
  }, [tierExpiresAt])

  // Re-evaluate whenever the tab becomes visible again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  return computeEntitlements(tier, tierExpiresAt, customFeatures)
}
